import React from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { GmailMessage } from '../../types/api';

interface FullscreenViewerModalProps {
  message: GmailMessage;
  onClose: () => void;
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1200;
  padding: ${props => props.theme.spacing[20]} ${props => props.theme.spacing[4]} ${props => props.theme.spacing[4]};
`;

const Modal = styled.div`
  width: min(680px, 100%);
  height: min(85vh, 100%);
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borders.radius.xl};
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundSecondary};
  flex-shrink: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  padding: ${props => props.theme.spacing[2]};
  border-radius: ${props => props.theme.borders.radius.md};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const Content = styled.div`
  flex: 1;
  overflow: hidden;
  background: ${props => props.theme.isLight ? '#ffffff' : '#000000'};
  display: flex;
  flex-direction: column;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${props => props.theme.spacing[6]};
  line-height: 1.6;
  font-size: 15px;
  
  /* 
     Strategy: Render email as "paper" (white bg, black text) by default.
     If dark mode, invert the entire container to make it dark.
     This handles HTML emails that hardcode white backgrounds.
  */
  background-color: #ffffff;
  color: #1e293b;
  
  ${props => !props.theme.isLight && `
    filter: invert(1) hue-rotate(180deg);
    
    /* Re-invert media to keep them looking natural */
    img, video, iframe, svg {
      filter: invert(1) hue-rotate(180deg);
    }
  `}

  pre {
    white-space: pre-wrap;
    margin: 0;
    font-family: inherit;
    color: inherit;
  }

  p {
    margin: ${props => props.theme.spacing[3]} 0;
  }

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }
`;

export const FullscreenViewerModal: React.FC<FullscreenViewerModalProps> = ({ message, onClose }) => {
  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <Title>{message.subject || '(sem assunto)'}</Title>
          <CloseButton onClick={onClose}>
            <X size={18} />
          </CloseButton>
        </Header>
        <Content>
          <Body>
            {message.bodyHtml ? (
              <div dangerouslySetInnerHTML={{ __html: message.bodyHtml }} />
            ) : (
              <pre>{message.body || 'Mensagem sem conteúdo.'}</pre>
            )}
          </Body>
        </Content>
      </Modal>
    </Overlay>
  );
};
